import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ReceiptText, Send } from 'lucide-react';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { requestSubscription } from '../../redux/subscriptionSlice';
import { BILLING_PERIODS } from '../../data/accessControls';
import { subscriptionPrice } from '../../utils/currency';

// Prices in the package's own currency, else the platform's (utils/currency.js, P24).
const money = (p, pkg) => (p == null || String(p) === '' ? 'On request' : (String(p) === '0' ? 'Free' : subscriptionPrice(p, pkg)));

// REQUEST A PLAN (owner, 22 Sep 2026 — Q8).
//
// This modal used to be a card form. There is no payment gateway on the
// platform and there is not going to be one, so a form that looked like a
// checkout and charged nothing was a lie told to the doctor: it said "Pay" and
// activated the plan on a fake card. What it does now is the honest version of
// the same step — pick the billing period, see the total, and ask. The platform
// e-mails an invoice; an admin marks it paid and the plan starts then.
//
// The period is part of the request (the old checkout never sent it, so the
// server priced whichever period it defaulted to).
const SubscriptionRequestModal = ({ pkg, doctorId, onClose, onSuccess, initialPeriod = null, lockPeriod = false }) => {
  const dispatch = useDispatch();
  const submitting = useSelector((s) => s.subscription.checkingOut);
  const sub = useSelector((s) => s.subscription.mySubscription);
  const [period, setPeriod] = useState(null);

  // Enabled billing periods for this package (admin-configured). Empty = legacy
  // single price/duration.
  const bp = pkg?.billingPeriods || {};
  const periods = BILLING_PERIODS.filter((p) => bp[p.id] && bp[p.id].enabled)
    .map((p) => ({ ...p, price: bp[p.id].price }));
  const hasPeriods = periods.length > 0;

  // Default the period: an admin-set pending change pins it (lockPeriod), else the
  // first option.
  useEffect(() => {
    if (hasPeriods && !period) {
      const pinned = initialPeriod && periods.some((p) => p.id === initialPeriod) ? initialPeriod : periods[0].id;
      setPeriod(pinned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg?.id]);

  const selected = periods.find((p) => p.id === period);
  const amount = hasPeriods ? selected?.price : pkg?.price;
  const durationDays = hasPeriods ? selected?.days : (pkg?.durationDays || 30);

  // Upgrade proration: credit the unused value of the current paid plan toward
  // this purchase. Mirrors the server (which recomputes + enforces it).
  const numAmount = parseFloat(amount);
  const isNumeric = Number.isFinite(numAmount);
  const remaining = Number(sub?.remainingValue) || 0;
  const credit = isNumeric ? Math.min(remaining, numAmount) : 0;
  const finalAmount = isNumeric ? Math.max(0, numAmount - credit) : 0;
  const dueLabel = isNumeric ? subscriptionPrice(finalAmount, pkg, { grouped: true }) : money(amount, pkg);

  const valid = !hasPeriods || !!selected;

  const submit = async () => {
    if (!valid) { toast.warn('Please choose a billing period'); return; }
    try {
      await dispatch(requestSubscription({
        doctorId, packageId: pkg.id, period: hasPeriods ? period : undefined,
      })).unwrap();
      toast.success(`Request sent — we will e-mail your invoice for ${pkg.name}`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not send the request — please try again');
    }
  };

  return (
    <ModalShell
      title={`Request ${pkg?.name || ''}`}
      onClose={onClose}
      size="xl"
      zClass="z-[100]"
      testId="request-modal"
      footer={(
        <ModalFooter
          onCancel={onClose}
          onSubmit={submit}
          submitLabel={submitting ? 'Sending…' : <><Send className="w-4 h-4" /> Request this plan</>}
          busy={submitting}
          disabled={!valid}
          submitTestId="request-submit"
        />
      )}
    >
        <div className="space-y-4">
          {/* Billing period selector (hidden when an admin pinned the period) */}
          {hasPeriods && !lockPeriod && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Billing period</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {periods.map((p) => {
                  const on = p.id === period;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriod(p.id)}
                      data-testid={`period-${p.id}`}
                      className={`text-left rounded-xl border px-3 py-2 transition-all ${on ? 'border-[#4ABDBD] ring-1 ring-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="text-sm font-semibold text-gray-700">{p.label}</div>
                      <div className="text-xs text-slate-500">{money(p.price, pkg)} · {p.days} days</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Plan summary + upgrade credit */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{pkg?.name} · {durationDays} days</span>
              <span className={credit > 0 ? 'text-slate-500 line-through' : 'font-semibold text-slate-700'}>{money(amount, pkg)}</span>
            </div>
            {credit > 0 && (
              <div className="flex items-center justify-between text-sm text-emerald-600" data-testid="request-credit">
                <span>Credit · {sub?.daysLeft} day{sub?.daysLeft === 1 ? '' : 's'} left on {sub?.packageName}</span>
                <span>−{subscriptionPrice(credit, pkg, { grouped: true })}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
              <span className="text-sm font-semibold text-[#1a365d]">Amount to invoice</span>
              <span className="text-lg font-bold text-[#1a365d]" data-testid="request-total">{dueLabel}</span>
            </div>
          </div>

          {/* How this is actually paid — said plainly, because there is no card step. */}
          <div className="flex items-start gap-2 text-xs text-[#2D8A8A] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 rounded-xl px-3 py-2.5" data-testid="request-invoice-note">
            <ReceiptText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>No card needed. We e-mail you an invoice — pay by UPI or bank transfer and your plan is activated.</span>
          </div>
        </div>

    </ModalShell>
  );
};

export default SubscriptionRequestModal;
